import { Category, SupplierMapping } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { Table, TableBody, TableCell, TableHead, TableRow, Theme } from '@mui/material';
import { getFilteredCategoryIds } from '@synergy/utils';
import { makeStyles } from '@mui/styles';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import * as SupplierCategoriesActions from './store/supplier-category-mapping-actions';
import { MappingEditModal } from '../../../../../common/components/mapping-edit-modal/mapping-edit-modal';
import { SupplierCategoryMappingTableRow } from './supplier-category-mapping-table-row';
import { supplierCategoryMappingSelector } from './store/supplier-category-mapping-selectors';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const useStyles = makeStyles((theme: Theme) => ({
    table: {
        backgroundColor: '#FFF',

        '& thead tr': {
            height: 48,
        },
        '& tbody tr': {
            height: 72,
        },
        '& th': {
            paddingTop: 8,
            paddingBottom: 0,
            whiteSpace: 'nowrap',
            fontSize: 12,
            backgroundColor: '#fff',
            '&:nth-child(n+2):nth-child(-n+3)': {
                paddingLeft: 24,
            },
            '&:first-child': {
                paddingLeft: theme.spacing(9),
            },
        },
        '& td:last-child': {
            paddingRight: 8,
        },
    },
}));

export const SupplierCategoryMapping = () => {
    const classes = useStyles();
    const { id: supplierIdParam } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();

    const categories = useAppSelector(supplierCategoryMappingSelector);
    const dispatch = useThunkDispatch();

    const [selectedMapping, setSelectedMapping] = useState<SupplierMapping<Category>>();

    useEffect(() => {
        if (supplierIdParam && +supplierIdParam && !categories) {
            dispatch(SupplierCategoriesActions.fetchSupplierCategoryMapping(+supplierIdParam));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supplierIdParam, categories]);

    if (!categories) {
        return null;
    }

    const handleMappingEdit = (mapping: string[]): void => {
        if (supplierIdParam && +supplierIdParam && selectedMapping) {
            const supplierMapping: SupplierMapping<Category> = {
                ...selectedMapping,
                supplierMapping: mapping,
            };
            dispatch(SupplierCategoriesActions.updateSupplierCategoryMapping(+supplierIdParam, supplierMapping));
        }
        setSelectedMapping(undefined);
    };

    const searchResult = getFilteredCategoryIds(categories, searchParams.get('search'));
    const topCategories = categories.filter(category => category.parentId === undefined);

    return (
        <>
            <Table className={classes.table} stickyHeader={true}>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <FormattedMessage id={'app.table.column.name'} />
                        </TableCell>
                        <TableCell width={'50%'}>
                            <FormattedMessage id={'app.table.column.mapping.default'} />
                        </TableCell>
                        <TableCell width={'50%'}>
                            <FormattedMessage id={'app.table.column.mapping.own'} />
                        </TableCell>
                        <TableCell width={'1%'} />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {topCategories.map(category => (
                        <SupplierCategoryMappingTableRow
                            key={category.id}
                            categories={categories}
                            category={category}
                            filterIds={searchResult}
                            depth={0}
                            onEdit={selectedCategory => setSelectedMapping(selectedCategory)}
                        />
                    ))}
                </TableBody>
            </Table>
            {selectedMapping && (
                <MappingEditModal
                    visible={!!selectedMapping}
                    mapping={selectedMapping.supplierMapping}
                    onClose={() => setSelectedMapping(undefined)}
                    onSubmit={handleMappingEdit}
                />
            )}
        </>
    );
};
